/**
 * ``/console`` — Platform Console parent route (full-screen ``_modal``
 * takeover, same group as ``/admin``).
 *
 * Gate: canonical entitlement denial redirects before the shell mounts.
 * API and network failures keep this URL and use the shared retry fallback.
 * The render guard repeats the access check without painting protected content.
 */

import { Outlet, createFileRoute } from "@tanstack/react-router";
import type * as React from "react";

import { getSession } from "@ando/core/auth";

import { hasConsoleAccess } from "@/features/console/access";
import { ensureConsoleAccess } from "@/features/console/lib/ensureConsoleAccess";
import { RouteErrorFallback } from "@/lib/RouteErrorFallback";
import { ConsoleShell } from "@/features/console/components/ConsoleShell";
import { ConsoleThemeProvider } from "@/features/console/theme/ConsoleTheme";

export const Route = createFileRoute("/_authed/_modal/console")({
  beforeLoad: ensureConsoleAccess,
  errorComponent: (props) => <RouteErrorFallback {...props} preserveLocation />,
  component: ConsoleArea,
});

function ConsoleArea(): React.ReactElement | null {
  // Defense in depth — same authoritative signal as beforeLoad.
  if (!hasConsoleAccess(getSession())) throw new Error("Accesso alla Console non disponibile.");
  return (
    <ConsoleThemeProvider>
      <ConsoleShell>
        <Outlet />
      </ConsoleShell>
    </ConsoleThemeProvider>
  );
}
